"use client"
import React from "react"
import API, { URL_PATH } from "../../services/API"
import moment from "moment"
import { Button, FormControl, Select, MenuItem, Typography } from "@mui/material"
import debounce from "lodash"
import Toast from "../../components/Toast"
import { initSocket } from "../../services/socket"
import Redux from "../../common/Redux"
import routes from "../../routes/URL"

// Define color scheme to match the previous table
const ColorScheme = {
  buttonColor: "#FFC857",
  bgColor: "#02111B",
  componentColor: "#07070A",
  accentColor: "#BB0A21",
  textColor: "#FFFFFF", 
  greenColor: "#4CAF50",
}

class HelloWorldScreen extends React.Component {
  constructor(props) {
    super(props)
    this.getPaymentModes = debounce.debounce(this.getPaymentModes, 1000)
    this.state = {
      data: [], // Main payment requests
      splitData: [], // Split payment requests
      paymentMode: "cash",
      paymentModes: [],
      search: "",
      toast: {
        open: false,
        message: "",
        severity: "success",
      },
      socketConnected: false,
    }

    this.socket = null
  }

  componentDidMount() {
    const hotel = this.props.hotels.find(h => h.hotel_slug === this.props.match.params.hotelSlug)
    if (!hotel) {
      this.props.history.push(routes.admin.dashboard.root(this.props.hotels[0]?.hotel_slug || ''))
      return
    }
    this.getPaymentRequests()
    this.getPaymentRequestsofSplit()
    this.getPaymentModes()
    this.initializeSocketIO()
  }

  componentWillUnmount() {
    this.cleanupSocketIO()
  }

  initializeSocketIO = () => {
    try {
      console.log("[Socket.IO] Initializing Socket.IO connection for payment requests...")
      const hotelId = this.props.selectedHotelId?.id
      if (!hotelId) {
        console.error("❌ Hotel ID not available for Socket.IO connection")
        return
      }

      this.socket = initSocket(String(hotelId))
      this.setupConnectionHandlers()
      this.subscribeToSocketIOChannels()
    } catch (error) {
      console.error("❌ Error initializing Socket.IO:", error)
    }
  }

  setupConnectionHandlers = () => {
    if (!this.socket) return

    this.socket.on("connect", () => {
      console.log("✅ Socket.IO connected successfully for payment requests")
      this.setState({ socketConnected: true })
      this.showToast("Connected to real-time payment updates", "success")
    })

    this.socket.on("disconnect", (reason) => {
      console.log("❌ Socket.IO disconnected:", reason)
      this.setState({ socketConnected: false })
    })

    this.socket.on("reconnect", (attemptNumber) => {
      console.log("🔄 Socket.IO reconnected after", attemptNumber, "attempts")
      this.setState({ socketConnected: true })
      this.showToast("Reconnected to real-time updates", "success")
      this.getPaymentRequests()
    })

    this.socket.on("connect_error", (error) => {
      console.error("❌ Socket.IO connection error:", error)
      this.setState({ socketConnected: false })
    })
  }

  subscribeToSocketIOChannels = () => {
    try {
      const hotelId = this.props.selectedHotelId?.id
      if (!hotelId || !this.socket) {
        console.error("❌ Hotel ID or Socket instance not available")
        return
      }

      console.log(`[Socket.IO] Subscribing to payment cash events for hotel: ${hotelId}`)
      this.socket.off(`etable_database_hotel.payment_cash.${hotelId}:hotel-events`)

      this.socket.on(`etable_database_hotel.payment_cash.${hotelId}:hotel-events`, (data) => {
        console.log("💰 Payment Event Received:", data)
        try {
          const parsedData = typeof data === "string" ? JSON.parse(data) : data
          if (parsedData.data.type === "object") {
            this.handleNewPaymentRequest(parsedData.data)
          } else if (parsedData.type === "message") {
            this.showToast(parsedData.message || "New payment request received", "info")
          } else {
            console.warn("🔴 Unknown payment event type:", parsedData.type)
          }
        } catch (error) {
          console.error("❌ Error parsing payment Socket.IO data:", error)
          console.log("🔴 Raw Payment Data:", data)
        }
      })

      console.log(`✅ Subscribed to payment cash events for hotel ${hotelId}`)
    } catch (error) {
      console.error("❌ Error subscribing to Socket.IO channels:", error)
    }
  }

  handleNewPaymentRequest = (orderData) => {
    console.log("💰 New payment request received via Socket.IO:", orderData)
    this.showToast("New payment request received!", "info")
    this.getPaymentRequests()
  }

  cleanupSocketIO = () => {
    try {
      console.log("[Socket.IO] Cleaning up payment connections...")
      if (this.socket) {
        const hotelId = this.props.selectedHotelId?.id
        if (hotelId) {
          this.socket.off(`etable_database_hotel.payment_cash.${hotelId}:hotel-events`)
        }

        this.socket.off("connect")
        this.socket.off("disconnect")
        this.socket.off("reconnect")
        this.socket.off("connect_error")

        this.socket = null
      }
      console.log("✅ Payment Socket.IO cleanup completed")
    } catch (error) {
      console.error("❌ Error during payment Socket.IO cleanup:", error)
    }
  }

  showToast = (message, severity = "success") => {
    this.setState({
      toast: {
        open: true,
        message,
        severity,
      },
    })
  }

  handleCloseToast = (event, reason) => {
    if (reason === "clickaway") {
      return
    }
    this.setState({
      toast: {
        ...this.state.toast,
        open: false,
      },
    })
  }

  getPaymentModes = async () => {
    const { status, message, data } = await API(this, "get", URL_PATH.getPaymentModes)
    if (status == "success") {
      this.setState({ paymentModes: data })
    } else {
      this.showToast(message, "error")
    }
  }

  getPaymentRequestsofSplit = async () => {
    const { status, message, data } = await API(this, "post", URL_PATH.getPaymentRequestsofSplit)
    if (status == "error") {
      this.props.okDialog(message)
    }
    this.setState({ splitData: data })
  }

  getPaymentRequests = async () => {
    const { status, message, data } = await API(this, "post", URL_PATH.getPaymentRequests)
    if (status == "error") {
      this.props.okDialog(message)
    }
    this.setState({ data })
  }

  paymentReceivedForOrder = async (item) => {
    const { id: orderId, mode = "cash" } = item
    this.setState(prevState => ({
      data: prevState.data.map(order => 
        order.id === orderId ? { ...order, isProcessing: true } : order
      ),
    }))

    const { status, message } = await API(this, "post", URL_PATH.paymentReceivedForOrder, {
      order_id: orderId,
      payment_method: mode,
    })

    this.setState(prevState => ({
      data: prevState.data.map(order => 
        order.id === orderId ? { ...order, isProcessing: false } : order
      ),
    }))

    if (status == "error") {
      return this.props.okDialog(message)
    }

    this.showToast("Payment accepted successfully!", "success")
    this.getPaymentRequests()
  }

  acceptSplitPayment = async (orderId) => {
    const orderIndex = this.state.splitData.findIndex(item => item.order_id === orderId)
    if (orderIndex === -1) return

    this.setState(prevState => {
      const newSplitData = [...prevState.splitData]
      newSplitData[orderIndex].isProcessing = true
      return { splitData: newSplitData }
    })

    try {
      const { status, message } = await API(this, "post", URL_PATH.confirmSplitPaymentReq, {
        order_id: orderId
      })

      if (status === "error") {
        this.showToast(message, "error")
      } else {
        this.showToast("Split payment accepted successfully!", "success")
        this.getPaymentRequests()
        this.getPaymentRequestsofSplit()
      }
    } catch (error) {
      this.showToast("Failed to accept split payment", "error")
    } finally {
      this.setState(prevState => {
        const newSplitData = [...prevState.splitData]
        newSplitData[orderIndex].isProcessing = false
        return { splitData: newSplitData }
      })
    }
  }

  handleSearchChange = (e) => {
    clearTimeout(this.timer)
    this.setState({ search: e.target.value }, () => {
      this.timer = setTimeout(() => {
        this.getPaymentRequests()
      }, 550)
    })
  }

  render() {
    return (
      <div
        className="container-fluid MuiBox-root css-0"
        style={{
          backgroundColor: ColorScheme.bgColor,
          color: ColorScheme.textColor,
          padding: "2rem",
          maxWidth: "full",
        }}
      >
        {/* Header with title and refresh buttons */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Typography variant="h5" style={{ color: ColorScheme.textColor }}>
            Payment Requests
          </Typography>
          <div>
            <Button
              variant="contained"
              style={{
                backgroundColor: ColorScheme.buttonColor,
                color: ColorScheme.componentColor,
                fontWeight: "700",
                marginRight: "1rem",
              }}
              onClick={() => this.getPaymentRequests()}
            >
              Refresh Main
            </Button>
            <Button
              variant="contained"
              style={{
                backgroundColor: ColorScheme.buttonColor,
                color: ColorScheme.componentColor,
                fontWeight: "700",
              }}
              onClick={() => this.getPaymentRequestsofSplit()}
            >
              Refresh Split
            </Button>
          </div>
        </div>

        {/* Main Payment Requests Section */}
        <Typography variant="h6" style={{ color: ColorScheme.textColor, margin: "2rem 0 1rem" }}>
          Main Payment Requests ({this.state.data.length})
        </Typography>
        {this.renderPaymentRequestsTable(this.state.data, false)}

        {/* Split Payment Requests Section */}
        <Typography variant="h6" style={{ color: ColorScheme.textColor, margin: "2rem 0 1rem" }}>
          Split Payment Requests ({this.state.splitData.length})
        </Typography>
        {this.renderSplitPaymentRequestsTable()}

        <Toast
          open={this.state.toast.open}
          message={this.state.toast.message}
          severity={this.state.toast.severity}
          onClose={this.handleCloseToast}
        />
      </div>
    )
  }

  renderPaymentRequestsTable(data, isSplit = false) {
    if (data.length === 0) {
      return (
        <div className="alert my-3 flex justify-center items-center" style={{ color: ColorScheme.textColor }}>
          No payment requests found
        </div>
      )
    }

    return (
      <div className="table-responsive mb-5">
        <table className="table">
          <thead>
            <tr>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Order ID
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Date
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Customer
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Type
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Amount
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Mode
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "transparent" : "#041A29",
                  borderBottom: "none",
                }}
              >
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle", height: "80px" }}>
                  {item.order_id || item.id}
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle", height: "80px" }}>
                  {moment(item.updated_at).format("DD/MM/YYYY HH:mm")}
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle", height: "80px" }}>
                  {item.user?.name || "N/A"}
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle", height: "80px" }}>
                  <div
                    style={{
                      backgroundColor: item.order_type == "dinein" ? "#691726" : "#d6a65a",
                      padding: "3px 12px",
                      borderRadius: "20px",
                      color: item.order_type == "dinein" ? "#fff" : "#000",
                      textAlign: "center",
                      width: "auto",
                      fontWeight: "700",
                    }}
                  >
                    {item.order_type == "dinein" ? "Dine-In" : "Pick-Up"}
                  </div>
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle", height: "80px" }}>
                  <strong>₹{item.final_total || item.total_order_amount}</strong>
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle", height: "80px" }}>
                  <FormControl variant="outlined" fullWidth size="small">
                    <Select
                      value={item.mode || "cash"}
                      onChange={(e) => {
                        const newData = [...data]
                        newData[index].mode = e.target.value
                        isSplit ? 
                          this.setState({ splitData: newData }) : 
                          this.setState({ data: newData })
                      }}
                      style={{
                        backgroundColor: "#02111B ",
                        color: ColorScheme.textColor,
                        border: `1px solid ${ColorScheme.buttonColor}`,
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            backgroundColor: "#02111B ",
                            backgroundImage: "none",
                            color: ColorScheme.textColor,
                          },
                        },
                      }}
                    >
                      {this.state.paymentModes.map((mode, idx) => (
                        <MenuItem 
                          key={idx} 
                          value={mode.value} 
                          style={{ color: ColorScheme.textColor }}
                        >
                          {mode.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle", height: "80px" }}>
                  <button
                    className="btn btn-sm d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: item.isProcessing ? "#2E7D32" : ColorScheme.greenColor,
                      color: ColorScheme.textColor,
                      width: "100%",
                      padding: "8px 0",
                      fontWeight: "700",
                      opacity: item.isProcessing ? 0.7 : 1,
                      cursor: item.isProcessing ? "not-allowed" : "pointer",
                    }}
                    onClick={() => {
                      if (!item.isProcessing) {
                        isSplit ? 
                          this.acceptSplitPayment(item.order_id) : 
                          this.paymentReceivedForOrder(item)
                      }
                    }}
                    disabled={item.isProcessing || (isSplit && !item.can_accept)}
                  >
                    {item.isProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Accepting
                      </>
                    ) : isSplit && !item.can_accept ? (
                      "Cannot Accept"
                    ) : (
                      isSplit ? "Accept Split" : "Accept"
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  renderSplitPaymentRequestsTable() {
    if (this.state.splitData.length === 0) {
      return (
        <div className="alert my-3 flex justify-center items-center" style={{ color: ColorScheme.textColor }}>
          No split payment requests found
        </div>
      )
    }

    return (
      <div className="table-responsive mb-5">
        <table className="table">
          <thead>
            <tr>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Order ID
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Total Amount
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Paid Amount
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Payers
              </th>
              <th style={{ color: ColorScheme.textColor, borderBottom: `1px solid ${ColorScheme.buttonColor}`, padding: "1rem" }}>
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {this.state.splitData.map((item, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "transparent" : "#041A29",
                  borderBottom: "none",
                }}
              >
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle" }}>
                  {item.order_id}
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle" }}>
                  <strong>₹{item.total_order_amount}</strong>
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle" }}>
                  <strong>₹{item.total_paid_amount}</strong>
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle" }}>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {item.payers.map((payer, payerIndex) => (
                      <div key={payerIndex} style={{ marginBottom: "0.5rem", padding: "0.5rem", backgroundColor: "#02111B", borderRadius: "4px" }}>
                        <div><strong>{payer.payer_name}</strong> ({payer.payer_role})</div>
                        <div>Amount: ₹{payer.amount_paid} ({payer.payment_method})</div>
                        <div>Paid at: {moment(payer.paid_at).format("DD/MM/YYYY HH:mm")}</div>
                        {payer.assigned_users && payer.assigned_users.length > 0 && (
                          <div style={{ marginTop: "0.5rem" }}>
                            <div><strong>Assigned Users:</strong></div>
                            <ul style={{ marginLeft: "1rem", marginBottom: 0 }}>
                              {payer.assigned_users.map((user, userIndex) => (
                                <li key={userIndex}>
                                  {user.user_name}: ₹{user.split_amount} ({user.payment_status})
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </td>
                <td style={{ padding: "1rem", border: "none", verticalAlign: "middle" }}>
                  <button
                    className="btn btn-sm d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: item.isProcessing ? "#2E7D32" : 
                                      item.can_accept ? ColorScheme.greenColor : "#BB0A21",
                      color: ColorScheme.textColor,
                      width: "100%",
                      padding: "8px 0",
                      fontWeight: "700",
                      opacity: item.isProcessing ? 0.7 : 1,
                      cursor: item.isProcessing || !item.can_accept ? "not-allowed" : "pointer",
                    }}
                    onClick={() => !item.isProcessing && item.can_accept && this.acceptSplitPayment(item.order_id)}
                    disabled={item.isProcessing || !item.can_accept}
                  >
                    {item.isProcessing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Accepting
                      </>
                    ) : item.can_accept ? (
                      "Accept Split"
                    ) : (
                      "Cannot Accept"
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
}

export default Redux(HelloWorldScreen)